import { SpanStatusCode } from '@opentelemetry/api';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { createSpan, endSpan } from 'src/common/helpers/tracing-helper';
import { getPendingPaymentRequest } from '@ncr-retail/common-payment-operations';
import { cancelPinpadOperation } from 'src/devices/payment/pinpad-operations';
import { CartStatus } from 'src/items-list/business-logic/service/enumerations/cart-status';
import {
  returnEwicNotification,
  showRefundModal,
} from 'src/tendering/business-logic/handle-refund-actions';
import { getIsEwicTenderInTbr } from 'src/tendering/helpers/tbr-ewic-tender';
import { getCurrentXLRConfig } from 'src/xlr-configuration/xlr-config';
import { EmeraldError } from '../../common/api/emerald-error';
import {
  ForcedExchangeIsNotCompleted,
  PaymentRequired,
  ReceiptMissingData,
  RefundPaymentRequired,
} from '../../common/helpers/error-code';
import { IReceiptArgs } from '../../common/interfaces/dtos/IReceiptArgs';
import { IRefundTenderGroup, ITender } from '../../common/interfaces/dtos/ITender';
import { store } from '../../common/redux/configure-store';
import { handleForcedExchange } from '../../force-exchange/business-logic/handle-forced-exchange';
import { handleDigitalReceiptPreference } from '../../receipt-options/business-logic/handle-receipt-preference';
import { setDefaultTenderState } from '../../tendering/business-logic/tendering-actions';
import { setHomePageTendering } from '../components/home-page-tendering';
import { applyCartResponse } from './common/apply-cart-response';
import { completeTransaction } from './complete-transaction';
import { getPaymentOptions } from './get-payment-options';
import { balanceTransaction } from './make-tender-payment';
import { getPaybackSellableTenders } from './service/get-payback-sellable-tenders';
import { transactionFinished } from './service/transaction-finished';
import { updateTransactionStatus } from './update-cart-status';
import { getCardData, hasCardData, resetCardData } from './../../devices/payment/get-card-details';
import { validateCard } from 'src/home-page/business-logic/validate-card';
import { processSwipeAheadData } from './process-swipe-ahead-data';

const hasReturnItems = () => {
  return !!store
    .getState()
    .itemList.ejItems.find(record => record.Type === EJRecordTypes.ReturnItem);
};

const hasRewardItems = () => {
  return !!store
    .getState()
    .itemList.ejItems.find(
      record => record.Type === EJRecordTypes.Coupon || EJRecordTypes.RewardAsTender,
    );
};

export const finishTransaction = async (
  isPrintReceipt: boolean,
  receiptsArgs?: IReceiptArgs,
  isCompleteTransaction?: boolean,
): Promise<void> => {
  const span = createSpan('finish-transaction');
  const { transactionId, transactionCustomer: customer } = store.getState().transaction;

  if (!receiptsArgs) {
    receiptsArgs = {
      printReceipt: isPrintReceipt,
      emailAddress: customer && customer.emailAddress,
      mobilePhoneNumber: customer && customer.mobilePhoneNumber,
    };
  }

  if (!getCurrentXLRConfig().finishTransactionOnlyWhenBalanceDueZero) {
    const { total } = store.getState().itemList;
    if (total !== 0) {
      await updateTransactionStatus(CartStatus.Tender);
    }
    if (total > 0) {
      return handleTenderCheckout().finally(() => endSpan(span));
    }
    if (total < 0 && hasReturnItems()) {
      return handleTenderRefund().finally(() => endSpan(span));
    }
    if (total < 0 && !hasReturnItems() && hasRewardItems()) {
      return handleRewardRefund(transactionId, isPrintReceipt).finally(() => endSpan(span));
    }
  }
  return transactionFinished(receiptsArgs, isCompleteTransaction)
    .then(async result => {
      await applyCartResponse(result);
      completeTransaction(result, isPrintReceipt);
    })
    .catch(async err => {
      if (err.code === ReceiptMissingData) {
        await handleReceiptOptions(err);
      } else if (err.code === PaymentRequired) {
        return handleTenderCheckout(err.data?.sellableTenders);
      } else if (err.code === RefundPaymentRequired) {
        return handleTenderRefund(err.data?.refundTenders);
      } else if (err.code === ForcedExchangeIsNotCompleted) {
        return handleForcedExchange(err.data);
      } else {
        span.setStatus({
          code: SpanStatusCode.ERROR,
          message: 'finishTransaction error',
        });
        throw err;
      }
    })
    .finally(() => endSpan(span));
};

const handleReceiptOptions = async (err: EmeraldError): Promise<void> => {
  const span = createSpan('handle-receipt-options');
  const noTender = {};
  const isTransactionFinished = true;
  await handleDigitalReceiptPreference(noTender, err, isTransactionFinished);
  endSpan(span);
};

const handleTenderCheckout = async (tenders?: ITender[]): Promise<void> => {
  const span = createSpan('handle-tender-checkout');

  await getPaymentOptions(tenders);

  if (getPendingPaymentRequest().includes('prompt-phone-number')) {
    await cancelPinpadOperation();
  }
  if (hasCardData()) {
    const cardDetails = getCardData();
    const isValid = await validateCard(cardDetails);
    if (!isValid) {
      resetCardData();
    }
  }
  setHomePageTendering();
  setDefaultTenderState();
  processSwipeAheadData();
  endSpan(span);
};

const handleRewardRefund = async (
  transactionId: string,
  isPrintReceipt: boolean,
): Promise<void> => {
  return balanceTransaction(transactionId).then(async result => {
    await applyCartResponse(result);
    completeTransaction(result, isPrintReceipt);
  });
};

export const handleTenderRefund = async (refundTenderGroup?: IRefundTenderGroup): Promise<void> => {
  if (getPendingPaymentRequest().includes('prompt-phone-number')) {
    await cancelPinpadOperation();
  }
  if (!refundTenderGroup) {
    refundTenderGroup = await getPaybackSellableTenders();
  }
  if (getIsEwicTenderInTbr()) {
    returnEwicNotification(refundTenderGroup);
  } else {
    showRefundModal(refundTenderGroup);
  }
};
